import {observable, action} from 'mobx';
import abortable from 'utils/abortable';
import makeAbortable from 'abortable-promise';

import {nonenumerable} from 'spa/decorators';

class IterableState {
  async postRequest(formData, campaignID, {isAborted}) {
    const {json} = await this.ctx.iterableSDK.post(`/add?campaign=${campaignID}`, formData);
    if (isAborted()) return;

    return json;
  }

  async getCampaignsRequest({isAborted}) {
    const {json} = await this.ctx.iterableSDK.get(`/get-messages`);

    if (isAborted()) return;

    this.embeddedPlacements = json && json.placements;

    this.receivedPlacements = true;

    return json;
  }

  async trackImpressionRequest(formData, {isAborted}) {
    const {json} = await this.ctx.iterableSDK.post(`/track/impression`, formData);
    if (isAborted()) return;

    return json;
  }

  async trackClickRequest(formData, {isAborted}) {
    const {json} = await this.ctx.iterableSDK.post(`/track/click`, formData);
    if (isAborted()) return;

    return json;
  }

  @action async getCampaigns() {
    const _abortable = abortable();
    this.abort = _abortable.abort;

    this.isLoading = true;
    this.error = null;

    const {promise} = makeAbortable(this.getCampaignsRequest(_abortable));
    this.lastLoad = promise;

    try {
      return await promise;
    } catch (error) {
      this.onError(error);
      throw error;
    } finally {
      this.always();
    }
  }

  /*
   * POST request to Iterable via iterable-sdk

    This will be sent to Iterable's API

    Request body to be sent in formData:

    {
      email: session && session.user ? session.user.email : anonymousUserEmail,
      messageId: `${specialityCampaign.metadata.campaignId}`,
      buttonIdentifier: '',
      targetUrl: '',
      deviceInfo: {
        platform: 'Web',
        deviceId: tracker.ctx.umDeviceIdentifier,
        appPackageName: 'Web',
      },
    }
   */
  @action async trackEmbeddedMessageClick(formData) {
    if (this.isLoadingTrackClick) return this.lastLoadTrackClick;

    const _abortableClick = abortable();
    this.abortClick = _abortableClick.abort;

    this.isLoadingTrackClick = true;
    this.errorTrackClick = null;

    const {promise} = makeAbortable(this.trackClickRequest(formData, _abortableClick));
    this.lastLoadTrackClick = promise;

    try {
      return await promise;
    } catch (error) {
      this.onErrorTrackClick(error);
      throw error;
    } finally {
      this.alwaysTrackClick();
    }
  }

  /*
   * POST request to Iterable via iterable-sdk

    This will be sent to Iterable's API

    Request body to be sent in formData:

    {
      email: session && session.user ? session.user.email : anonymousUserEmail,
      session: {
        id: tracker.sessionID,
        start: 0,
        end: 0,
      },
      impressions: [
        {
          messageId: `${specialityCampaign.metadata.campaignId}`,
          placementId,
          displayCount: 1,
          displayDuration: 0,
        },
      ],
      deviceInfo: {
        platform: 'Web',
        deviceId: tracker.ctx.umDeviceIdentifier,
        appPackageName: 'Web',
      },
    }
   */
  @action async trackEmbeddedMessageImpression(formData) {
    if (this.isLoadingTrackImpression) return this.lastLoadTrackImpression;

    const _abortableImpression = abortable();
    this.abortImpression = _abortableImpression.abort;

    this.isLoadingTrackImpression = true;
    this.errorTrackImpression = null;

    const {promise} = makeAbortable(this.trackImpressionRequest(formData, _abortableImpression));
    this.lastLoadTrackImpression = promise;

    try {
      return await promise;
    } catch (error) {
      this.onErrorTrackImpression(error);
      throw error;
    } finally {
      this.alwaysTrackImpression();
    }
  }

  /*
   * POST request to Iterable via iterable-sdk
   */
  @action async post(formData, campaignID) {
    if (this.isLoading) return this.lastLoad;

    const _abortable = abortable();
    this.abort = _abortable.abort;

    this.isLoading = true;
    this.error = null;

    const {promise} = makeAbortable(this.postRequest(formData, campaignID, _abortable));
    this.lastLoad = promise;

    try {
      return await promise;
    } catch (error) {
      this.onError(error);
      throw error;
    } finally {
      this.always();
    }
  }

  // Get the embedded messages campaigns of this user for specific placementId
  @action getCampaignsForPlacementId(placementId) {
    if (!this.embeddedPlacements) return [];

    const [placement] = this.embeddedPlacements.filter(placement => placement.placementId === placementId);

    return placement ? placement.embeddedMessages : [];
  }

  @observable isLoading = false;
  @observable error = null;

  @observable embeddedPlacements;
  @observable receivedPlacements = false;

  @nonenumerable listeners = [];

  @action onError(error) {
    this.error = error;
  }
  @action always() {
    this.isLoading = false;
  }

  @action onErrorTrackClick(error) {
    this.errorTrackClick = error;
  }
  @action alwaysTrackClick() {
    this.isLoadingTrackClick = false;
  }

  @action onErrorTrackImpression(error) {
    this.errorTrackImpression = error;
  }
  @action alwaysTrackImpression() {
    this.isLoadingTrackImpression = false;
  }

  @action reset() {
    if (this.abort) this.abort();
    this.error = null;
    this.isLoading = false;
  }

  constructor(ctx) {
    Object.defineProperty(this, 'ctx', {
      value: ctx,
      enumerable: false,
    });
  }
}

export default IterableState;



// WEBPACK FOOTER //
// ./spa/state/_util/iterable.jsx